import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_exporting from 'highcharts/modules/exporting';
import HC_boost from 'highcharts/modules/boost';
import { useTheme } from '../../providers/theme';
import { getColors } from '../utils/getColors';
import { buildChartOptions, ChartSpec } from './chartSpec';

// Single place the Highcharts modules are initialised for the whole app.
// `boost` (canvas/WebGL) stays inert until a chart opts in via ChartSpec.boost
// and its thresholds are exceeded, so loading it here costs nothing otherwise.
if (typeof Highcharts === 'object') {
  HC_exporting(Highcharts);
  HC_boost(Highcharts);
}

export type ChartProps = {
  spec: ChartSpec;
  /** Forwarded to the underlying HighchartsReact (for chart.export()/redraw access). */
  chartRef?: React.Ref<HighchartsReact.RefObject>;
  /** Forwarded to HighchartsReact's wrapping container element. */
  containerProps?: HighchartsReact.Props['containerProps'];
};

/**
 * The single themed chart primitive. Reads the active theme, builds a
 * `Highcharts.Options` from the declarative spec, and renders the chart.
 * Every chart in the app should go through this — no component should import
 * `highcharts` directly.
 */
export const Chart: React.FC<ChartProps> = ({ spec, chartRef, containerProps }) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const options = useMemo(() => buildChartOptions(spec, colors), [spec, colors]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
      containerProps={containerProps}
    />
  );
};

export default Chart;
